/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useSkeem } from "../providers/SkeemProvider"
import React, { useState } from "react"
import { IRecord } from "../types/schema"
import { siteColors } from "../providers/ThemeProvider"
import { NewAsyncSelectField } from "../interfaceParts/_NewAsyncSelectField"
import { useAsyncEffect } from "../hooks/useAsyncEffect"
import { useGlobalState } from "../utils/globalState"
import { NewAbstractMultiSelectField } from "../interfaceParts/_NewAbstractMultiSelectField"

type ISort = { direction: "asc" | "desc"; by: any }

interface ICreateSelectFieldOpts<RecordType> {
  model: string
  attributes: any[] | readonly any[]
  queryToSkeemFilter(query: string): any
  getSkeemSort(query: string): ISort | ISort[] | null
  renderOption(record: RecordType): React.ReactNode
  scopeToCompany?: boolean
  optionPadding?: number
}

export function createSelectField<RecordType extends IRecord<any>>(
  opts: ICreateSelectFieldOpts<RecordType>,
) {
  type ISelectFieldProps<Additional> = {
    label?: React.ReactNode | null
    skeemFilter?: any
    value?: string | null
    error?: string | boolean
    showRequiredStar?: boolean
    disabled?: boolean
    placeholder?: string
    additionalAttributes?: any[]
    noResultsMessage?: string
    // If specified then this will be used to determine the selected value
    // including the skeem fetch to lookup any current value. This only should
    // be used for attributes which are unique, if not then there is no
    // guarantee that it will return the correct value.
    //
    // It defaults to "id"
    valueAttribute?: keyof RecordType
    dontLoadUntilOpen?: boolean
  } & (
    | {
        isClearable?: false
        onChange(item: RecordType & Additional): void
      }
    | {
        isClearable: true
        onChange(item: (RecordType & Additional) | null): void
      }
  )

  function Component<AdditionalRecord>(props: ISelectFieldProps<AdditionalRecord>) {
    const globalState = useGlobalState()
    const valueAttribute = props.valueAttribute || "id"

    const skeem = useSkeem()
    const [valueRecord, setValueRecord] = useState<RecordType | null | "not-found">(null)

    // Watch the value and load the data needed to display it
    useAsyncEffect(async () => {
      if (!props.value) {
        return
      }
      // the valueRecord already matches
      if (
        valueRecord &&
        valueRecord !== "not-found" &&
        props.value === valueRecord[valueAttribute]
      ) {
        return
      }

      const record = await skeem.fetch2<RecordType | null>({
        first: {
          name: opts.model,
          attributes: opts.attributes,
          filter: { matches: { [valueAttribute]: props.value } },
        },
      })

      if (record === null) {
        setValueRecord("not-found")
      } else {
        setValueRecord(record)
      }
    }, [props.value])

    function handleChange(newValue: RecordType & AdditionalRecord) {
      props.onChange(newValue)
      setValueRecord(newValue)
    }

    async function handleLoadOptions(query: string) {
      const records = await skeem.fetch<RecordType & AdditionalRecord>({
        [opts.model]: {
          attributes: [...opts.attributes, ...(props.additionalAttributes ?? [])],
          filter: {
            and: [
              opts.queryToSkeemFilter(query),
              props.skeemFilter,
              opts.scopeToCompany ? { matches: { company: globalState.companyId } } : null,
            ].filter(Boolean),
          },
          paginate: { size: 50 },
          sort: opts.getSkeemSort(query) ?? undefined,
        },
      })

      return records
    }

    const reloadKey = `${JSON.stringify(props.additionalAttributes)}-${JSON.stringify(
      props.skeemFilter,
    )}`
    return (
      <NewAsyncSelectField
        reloadOptionsKey={reloadKey}
        disabled={props.disabled}
        label={props.label}
        showRequiredStar={props.showRequiredStar}
        value={props.value ? valueRecord || "loading" : null}
        keyExtractor={record => (record === "not-found" ? "not-found" : record.id)}
        error={props.error}
        isClearable={props.isClearable}
        onChange={handleChange as any}
        loadOptions={({ query }) => handleLoadOptions(query)}
        optionPadding={opts.optionPadding}
        renderOption={item =>
          item === "not-found" ? (
            <div
              css={css`
                color: white;
                background: ${siteColors.red};
                padding: 6px;
              `}
            >
              Could not find record with {valueAttribute} = <b>{props.value}</b>
            </div>
          ) : (
            opts.renderOption(item)
          )
        }
        noResultsMessage={props.noResultsMessage}
        dontLoadUntilOpen={props.dontLoadUntilOpen}
      />
    )
  }

  Component.Multi = function MultiSelectField({
    label,
    value,
    onChange,
    skeemFilter,
    enableSelectAllFiltered,
    disabled,
  }: {
    label?: string
    value: string[]
    onChange: (records: RecordType[]) => void
    skeemFilter?: any
    enableSelectAllFiltered?: boolean
    disabled?: boolean
  }) {
    const skeem = useSkeem()
    const [valueRecords, setValueRecords] = useState<RecordType[]>([])
    const [query, setQuery] = useState("")
    const [results, setResults] = useState<{ records: RecordType[]; query: string } | null>(null)

    useAsyncEffect(
      async ({ isAlive }) => {
        if (value.length === 0) {
          setValueRecords([])
          return
        }

        const currentIds = valueRecords.map(user => user.id)
        const missingIds = value.filter(id => !currentIds.includes(id))

        if (missingIds.length > 0) {
          const records = await skeem.fetch2<RecordType[]>({
            model: {
              name: opts.model,
              attributes: opts.attributes,
              matches: { id: { or: value } },
            },
          })
          if (!isAlive()) {
            return
          }

          setValueRecords(records)
        } else {
          setValueRecords(valueRecords.filter(user => value.includes(user.id)))
        }
      },
      [JSON.stringify(value)],
    )

    useAsyncEffect(
      async ({ isAlive }) => {
        await new Promise(resolve => setTimeout(resolve, 500))
        if (!isAlive()) return

        const records = await skeem.fetch2<RecordType[]>({
          model: {
            name: opts.model,
            attributes: opts.attributes,
            filter: {
              and: [opts.queryToSkeemFilter(query), skeemFilter].filter(Boolean),
            },
            paginate: { size: 50 },
            sort: opts.getSkeemSort("") ?? undefined,
          },
        })

        setResults({ records, query })
      },
      [query],
    )

    return (
      <NewAbstractMultiSelectField
        label={label}
        value={valueRecords}
        onChange={onChange}
        disabled={disabled}
        keyExtractor={record => record.id}
        options={results?.records ?? []}
        renderOption={opts.renderOption}
        filterActions={
          enableSelectAllFiltered ? (
            <button
              css={css`
                background: transparent;
                border: none;
                cursor: pointer;
                font-size: 13px;
                padding: 6px 8px;
                border-radius: 6px;
                &:hover {
                  background: rgba(0, 0, 0, 0.04);
                }
              `}
              type="button"
              onClick={e => {
                e.preventDefault()
                e.stopPropagation()
                const options = results?.records ?? []
                if (options.length === 0) return
                const current = valueRecords ?? []
                const map = new Map<string, RecordType>()
                current.forEach(r => map.set(r.id, r))
                options.forEach(r => map.set(r.id, r))
                onChange(Array.from(map.values()))
              }}
            >
              Select all filtered
            </button>
          ) : null
        }
        filter={{
          value: query,
          onChange: setQuery,
          isLoading: !results || results?.query !== query,
        }}
      />
    )
  }

  return Component
}
